import {
  AfterViewInit,
  Component,
  ComponentRef,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core'
import * as lodash from 'lodash'
import { SignFieldState } from '../fields/sign-field-state.model'
import { Subject } from 'rxjs'
import { debounceTime, filter } from 'rxjs/operators'
import { FieldPlaceholderComponent, FieldPlaceholderData } from '../fields/field-placeholder.component'
import { defaultFieldFont, defaultFieldFontSize, RoleColor } from '../fields/constants'

import Panzoom, { PanzoomObject, PanzoomOptions } from '@panzoom/panzoom'
import {
  FieldActionData,
  FieldActionType,
  FieldsEditorActionManagerService,
} from './fields-editor-action-manager.service'
import { signFieldComponents } from '../fields'
import { FieldsAlignManagerService, FieldsAlignment } from './fields-select-manager.service'
import { PrefillFieldPlaceholderComponent } from '../fields/prefill-field-placeholder/prefill-field-placeholder.component'
import {
  DragDropManagerService,
  SignatureRole,
  serializeTemplateFields,
  Settings,
  unserializeTemplateFields,
} from '@tokeet-frontend/signature'
import { SignFieldDef, SignFieldRect, SignFieldType } from '@tokeet-frontend/signature'
import { Destroyable, untilDestroy } from '@tokeet-frontend/tv3-platform'
import { TemplateTypes } from '@tokeet-frontend/signature'
import { SignPrepareService } from '@tv3/services/sign-prepare.service'
import { SignatureDataDictHelperService } from '@tv3/services/signature-data-dict-helper.service'

export interface DroppedSignFieldData {
  type: SignFieldType
  label: string
  attributes: any
}

@Component({
  selector: 'app-template-fields-editor',
  templateUrl: './template-fields-editor.component.html',
  styleUrls: ['./template-fields-editor.component.scss'],
  providers: [FieldsAlignManagerService],
})
export class TemplateFieldsEditorComponent extends Destroyable implements OnInit, AfterViewInit {
  @Input() templateType: TemplateTypes
  @Input() templateFileUrl: string
  _fields: SignFieldDef[]
  @Input() set fields(items: SignFieldDef[]) {
    this._fields = unserializeTemplateFields(items)
  }
  get fields() {
    return this._fields
  }

  @Input() roleColors: RoleColor[]
  @Input() currentRole: string
  @Input() font: string
  @Input() fontSize: number
  @Input() defaultField: DroppedSignFieldData

  // roles: string[] = [];

  @ViewChild('zoomContainer') zoomContainer: ElementRef
  @ViewChild('docContainer') docContainer: ElementRef
  @ViewChild('selectBox') selectBox: ElementRef
  @ViewChild('signFieldsContainer', { read: ViewContainerRef }) signFieldsContainer: ViewContainerRef
  get zoomContentRef() {
    return this.docContainer
  }

  private role2ColorMapping: { [key: string]: string } = {}
  fieldRefs: ComponentRef<FieldPlaceholderComponent>[] = []

  isLoading = true
  isEditorReady = false

  refreshTemplateFields$ = new Subject()

  minZoom = 0.5
  maxZoom = 2
  zoomController: PanzoomObject

  alignments = FieldsAlignment

  constructor(
    public signFieldManager: DragDropManagerService,
    private signPrepareService: SignPrepareService,
    private dataDicHelper: SignatureDataDictHelperService,
    public actionManager: FieldsEditorActionManagerService,
    private alignManager: FieldsAlignManagerService
  ) {
    super()
  }

  ngOnInit(): void {
    this.refreshTemplateFields$.pipe(debounceTime(1000), untilDestroy(this)).subscribe(() => {
      this.renderTemplateFields()
      this.isEditorReady = true
    })
    this.signFieldManager.drop$
      .pipe(
        filter(() => !this.isLoading),
        untilDestroy(this)
      )
      .subscribe(({ el, data }: { el: Element; data: DroppedSignFieldData }) => {
        this.onFieldDropped(el, data.type, data.label, data.attributes)
      })
    this.actionManager.clear()
  }

  ngAfterViewInit() {
    this.signFieldManager.addDroppableContainer(this.docContainer)
    this.alignManager.init(this.docContainer, this.selectBox)

    this.zoomController = Panzoom(this.zoomContentRef.nativeElement, {
      increment: 0.1,
      minScale: this.minZoom,
      maxScale: this.maxZoom,
      origin: '0 0',
      // contain: 'inside',
      cursor: 'default',
      overflow: 'auto',
      disablePan: true,
      animate: true,
      duration: 300,
      handleStartEvent: () => {},
      setTransform: (elem, { scale, x, y }) => {
        if (this.zoomController) {
          this.zoomController.setStyle('transform', `scale(${scale}) translate3d(0px, 0px, 0px)`)
        }
      },
    } as PanzoomOptions)
  }

  onDocScroll() {
    window.dispatchEvent(new Event('scroll'))
  }

  getDefaultReadonlyValueForField(isEditableGuestDataDictField: boolean) {
    return isEditableGuestDataDictField ? { readonly: this.templateType !== TemplateTypes.Form } : {}
  }

  renderTemplateFields() {
    this.role2ColorMapping = lodash.reduce(
      this.roleColors,
      (res, data, i) => {
        res[data.role] = data.color
        return res
      },
      {}
    )
    lodash.forEach(this.fields, (field, index) => {
      const isEditableGuestDataDictField = this.isEditableGuestDataDictField(
        this.templateType,
        field.type,
        field.attributes?.key
      )
      this.renderField(
        field.type,
        { ...lodash.pick(field, ['x', 'y', 'width', 'height']) },
        {
          roleColors: this.roleColors,
          role2ColorMapping: this.role2ColorMapping,
          ...lodash.pick(field, ['required', 'label', 'role', 'value', 'attributes', 'font', 'font_size']),
          ...(lodash.has(field, 'readonly')
            ? { readonly: field.readonly }
            : this.getDefaultReadonlyValueForField(isEditableGuestDataDictField)),
        } as SignFieldState,
        `template-field-${index}`
      )
    })
  }

  onDocClick($event: MouseEvent) {
    if (!this.defaultField || !($event.target instanceof HTMLCanvasElement)) {
      return
    }
    const droppedContainerElement = this.docContainer.nativeElement
    const containerViewPortOffset = droppedContainerElement.getBoundingClientRect()
    let left = $event.clientX - containerViewPortOffset.left
    let top = $event.clientY - containerViewPortOffset.top

    const currentScale = this.zoomController.getScale()
    left = left / currentScale
    top = top / currentScale

    this.addField(left, top, this.defaultField.type, this.defaultField.label, this.defaultField.attributes)
  }

  onFieldDropped(droppedElement: Element, fieldType: SignFieldType, label: string, attributes: any = {}) {
    // these are relative to the viewport, i.e. the window
    const fieldViewportOffset = droppedElement.getBoundingClientRect()
    const droppedContainerElement = this.docContainer.nativeElement
    const containerViewPortOffset = droppedContainerElement.getBoundingClientRect()
    let left = fieldViewportOffset.left - containerViewPortOffset.left
    let top = fieldViewportOffset.top - containerViewPortOffset.top

    const currentScale = this.zoomController.getScale()
    left = left / currentScale
    top = top / currentScale

    this.addField(left, top, fieldType, label, attributes)
  }

  isEditableGuestDataDictField(templateType: TemplateTypes, fieldType: SignFieldType, fieldKey: string) {
    return (
      fieldType === SignFieldType.DataDic &&
      [TemplateTypes.Contract, TemplateTypes.Form].includes(templateType) &&
      this.dataDicHelper.isGuestField(fieldKey)
    )
  }

  private addField(left: number, top: number, fieldType: SignFieldType, label: string, attributes: any = {}) {
    // if template type is 'contract' or 'document', datadic field is filled with tokeet data
    // if template type is 'form', guest datadic field is filled by signer, custom code fields are filled with tokeet data.
    const isEditableGuestDataDictField = this.isEditableGuestDataDictField(
      this.templateType,
      fieldType,
      lodash.get(attributes, 'key', '')
    )
    let fieldRole
    if (
      ![SignFieldType.DataDic, SignFieldType.Prefill].includes(fieldType) ||
      (isEditableGuestDataDictField && !SignatureRole.isUserRole(this.currentRole))
    ) {
      fieldRole = this.currentRole
    }

    const x2HeightTypes = [SignFieldType.Signature, SignFieldType.Initials]

    this.renderField(
      fieldType,
      {
        width: fieldType === SignFieldType.Checkbox ? Settings.CHECKBOX_FIELD_WIDTH : Settings.DEFAULT_FIELD_WIDTH,
        height: lodash.includes(x2HeightTypes, fieldType)
          ? 2 * Settings.DEFAULT_FIELD_HEIGHT
          : Settings.DEFAULT_FIELD_HEIGHT,
        x: left,
        y: top,
      },
      {
        roleColors: this.roleColors || [],
        role2ColorMapping: this.role2ColorMapping,
        label,
        role: fieldRole,
        attributes,
        font: this.font || defaultFieldFont.id,
        font_size: this.fontSize || defaultFieldFontSize,
        ...this.getDefaultReadonlyValueForField(isEditableGuestDataDictField),
      }
    )
  }

  private onFieldRemove(componentRef: ComponentRef<FieldPlaceholderComponent>) {
    this.fieldRefs = this.fieldRefs.filter((item) => {
      return item !== componentRef
    })
    this.alignManager.removeField(componentRef)
    componentRef.destroy()
  }

  private renderField(type: SignFieldType, rect: SignFieldRect, initialState: SignFieldState, id?: string) {
    const componentRef = this.signPrepareService.appendTo<FieldPlaceholderComponent>(
      signFieldComponents[type],
      this.signFieldsContainer
    )
    this.alignManager.addField(componentRef)
    const { instance } = componentRef
    if (id) {
      instance.id = id
    }
    this.fieldRefs.push(componentRef)
    instance.remove.subscribe(() => this.onFieldRemove(componentRef))
    // set bounds element
    instance.boundsRef = this.docContainer.nativeElement
    const { roleColors = [] } = initialState
    instance.roleColors = roleColors
    instance.label = initialState.label || instance.label
    instance.required = lodash.isBoolean(initialState.required) ? initialState.required : instance.required
    instance.role2ColorMapping = initialState.role2ColorMapping

    if (instance.type === SignFieldType.Prefill) {
      ;(instance as PrefillFieldPlaceholderComponent).prefillType = initialState.attributes.prefill_type
      ;(instance as PrefillFieldPlaceholderComponent).patchInitialData(initialState)
    }

    const data: FieldPlaceholderData = {
      role: initialState.role,
      value: initialState.value,
      attributes: initialState.attributes || {},
      font: initialState.font,
      font_size: initialState.font_size,
      readonly: initialState.readonly,
    }
    instance.data = data
    instance.isEditableGuestDataDictField = this.isEditableGuestDataDictField(
      this.templateType,
      type,
      lodash.get(initialState, 'attributes.key', '')
    )
    instance.isRoleEditable = !instance.isEditableGuestDataDictField

    if (!initialState.role) {
      instance.disableCfg = true
    }

    instance.setRect(rect)
  }

  getFieldsConfig(): SignFieldDef[] {
    const fields = this.fieldRefs.map((componentRef) => {
      const { instance } = componentRef
      return instance.getFieldConfig()
    })

    return serializeTemplateFields(fields)
  }

  isTemplateReady() {
    if (lodash.isEmpty(this.roleColors)) {
      return false
    }

    const fields = this.fieldRefs.map((componentRef) => {
      const { instance } = componentRef
      return { role: instance.role, type: instance.type }
    })
    return this.signPrepareService.isTemplateReady(
      this.templateType,
      lodash.map(this.roleColors, (t) => t.role),
      fields
    )
  }

  onTemplateFileLoaded() {
    this.isLoading = false
    this.refreshTemplateFields$.next()
  }

  scrollIntoView() {
    const docElement: HTMLDivElement = this.docContainer.nativeElement
    docElement.scrollIntoView({ behavior: 'smooth', inline: 'center' })
  }

  doAction(action: FieldActionType, fieldId: string, data?: FieldActionData) {
    const com = this.fieldRefs.find((c) => c.instance.id === fieldId)
    const rect = { x: data?.x, y: data?.y, width: data?.width, height: data?.height }
    switch (action) {
      case 'add':
        this.renderField(
          data.type,
          rect,
          {
            roleColors: this.roleColors || [],
            role2ColorMapping: this.role2ColorMapping,
            label: data.label,
            role: data.role,
            attributes: data.attributes,
            font: data.font,
            font_size: data.font_size,
            readonly: data.readonly,
          },
          fieldId
        )
        break
      case 'remove':
        com.instance.remove.emit()
        break
      case 'move':
        com.instance.doMove(rect)
        break
      case 'resize':
        com.instance.doResize(rect)
        break
      case 'settings':
        com.instance.setSettings(data)
        break
      default:
        break
    }
  }

  onAlign(alignment: FieldsAlignment) {
    this.alignManager.doAlign(alignment)
  }

  onRedo() {
    this.alignManager.reset()
    const actionItems = this.actionManager.getRedoData()
    lodash.forEach(actionItems, ({ type, fieldId, after }) => {
      this.doAction(type, fieldId, after)
    })
  }

  onUndo() {
    this.alignManager.reset()
    const actionItems = this.actionManager.getUndoData()
    lodash.forEach(actionItems, ({ type, fieldId, before }) => {
      const action = type === 'add' ? 'remove' : type === 'remove' ? 'add' : type
      this.doAction(action, fieldId, before)
    })
  }

  onZoomToFitScreen() {
    const containerRect = this.zoomContainer.nativeElement.getBoundingClientRect()
    const contentRect = this.zoomContentRef.nativeElement.getBoundingClientRect()
    const widthScale = containerRect.width / contentRect.width
    this.zoomController.zoom(widthScale)
    this.refreshFieldsZoomScale()
    setTimeout(() => this.scrollIntoView(), 300)
  }

  onResetZoom() {
    this.zoomController.reset()
    this.refreshFieldsZoomScale()
  }

  onZoomOut() {
    this.zoomController.zoomOut()
    this.refreshFieldsZoomScale()
  }

  onZoomIn() {
    this.zoomController.zoomIn()
    this.refreshFieldsZoomScale()
  }

  refreshFieldsZoomScale() {
    const zoomScale = this.zoomController.getScale()
    lodash.forEach(this.fieldRefs, (com) => {
      com.instance.zoomScale = zoomScale
    })
  }
}
